"use client";

import { BillingFrequencyToggle } from "@/components/paywall/BillingFrequencyToggle";
import { type PackageType, REVENUECAT_CONFIG } from "@/lib/revenuecat/config";
import { type PackageDisplayPricing } from "@/lib/revenuecat/package-pricing";
import { Check } from "lucide-react";

const PERIOD_LABELS: Record<PackageType, string> = {
  annual: "per month",
  monthly: "per month",
  weekly: "per week",
};

const BILLING_COPY: Record<PackageType, string> = {
  annual: "One payment covers 12 months.",
  monthly: "Renews monthly. Cancel anytime.",
  weekly: "Renews weekly. Cancel anytime.",
};

const MEMBERSHIP_HIGHLIGHTS = [
  "Unlimited conversations",
  "70+ languages",
  "Premium voices",
  "Session history",
];

function formatCurrencyAmount(amount: number, currencyCode: string) {
  try {
    return new Intl.NumberFormat("en", {
      currency: currencyCode,
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
      style: "currency",
    }).format(amount);
  } catch {
    return `${currencyCode} ${amount.toFixed(2)}`;
  }
}

export function MembershipOfferCard({
  annualSavingsPercent,
  disabled = false,
  isLoading = false,
  onBillingChange,
  onPurchase,
  packageType,
  pricing,
  showFreeTrial = false,
}: {
  annualSavingsPercent: number | null;
  disabled?: boolean;
  isLoading?: boolean;
  onBillingChange: (packageType: PackageType) => void;
  onPurchase: (packageType: PackageType) => void;
  packageType: PackageType;
  pricing: PackageDisplayPricing;
  showFreeTrial?: boolean;
}) {
  const pkg = REVENUECAT_CONFIG.packages[packageType];
  const isAnnual = packageType === "annual";
  const headlinePrice = isAnnual
    ? formatCurrencyAmount(pricing.priceAmount / 12, pricing.currencyCode)
    : pricing.priceFormatted;

  return (
    <article className="flex h-full flex-col rounded-2xl border border-white/15 bg-[#15161B] p-6 md:p-8">
      <header className="md:min-h-32">
        <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#FD954F]">
          Independent practice
        </p>
        <h3 className="mt-3 text-2xl font-bold text-white">AI Speaking Membership</h3>
        <p className="mt-2 max-w-md text-sm leading-relaxed text-[#AEAEB2]">
          Unlimited speaking practice in 70+ languages.
        </p>
      </header>

      <div className="mt-6 border-t border-white/10 pt-5">
        {annualSavingsPercent !== null && (
          <div className="mb-2 grid grid-cols-3 px-1">
            <button
              className="col-span-2 col-start-1 w-max max-w-full cursor-pointer rounded-md border border-[#FD954F]/30 bg-[#FD954F]/10 px-2 py-1 text-left text-[11px] font-bold leading-4 text-[#FD954F] transition-colors hover:border-[#FD954F]/55 hover:bg-[#FD954F]/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FD954F] focus-visible:ring-offset-2 focus-visible:ring-offset-[#15161B] disabled:cursor-not-allowed disabled:opacity-60"
              disabled={disabled || isLoading}
              onClick={() => onBillingChange("annual")}
              type="button"
            >
              Save {annualSavingsPercent}% by going annual
            </button>
          </div>
        )}

        <BillingFrequencyToggle
          disabled={disabled || isLoading}
          onChange={onBillingChange}
          value={packageType}
        />
      </div>

      <section className="mt-5">
        <div className="flex flex-wrap items-baseline gap-2">
          {!isAnnual && pricing.originalPriceFormatted && (
            <span className="text-lg font-semibold text-white/35 line-through">
              {pricing.originalPriceFormatted}
            </span>
          )}
          <span className="text-4xl font-bold text-white">{headlinePrice}</span>
          <span className="text-sm text-[#AEAEB2]">{PERIOD_LABELS[packageType]}</span>
        </div>
        {isAnnual && (
          <p className="mt-2 text-sm text-white/75">
            {pricing.priceFormatted} billed annually
            {pricing.originalPriceFormatted ? (
              <span className="ml-2 text-white/35 line-through">
                {pricing.originalPriceFormatted}
              </span>
            ) : null}
          </p>
        )}
        <p className="mt-2 text-xs text-[#AEAEB2]">{BILLING_COPY[packageType]}</p>
        {(showFreeTrial || pricing.hasDiscountPrice) && (
          <div className="mt-3 space-y-1 text-xs font-semibold text-emerald-300">
            {showFreeTrial && <p>Free for 3 days</p>}
            {pricing.hasDiscountPrice && (
              <p>
                Promotional pricing applied
                {pricing.promotionalPeriod ? ` for ${pricing.promotionalPeriod}` : ""}
              </p>
            )}
          </div>
        )}
      </section>

      <ul className="my-6 grid grid-cols-2 gap-x-4 gap-y-3 border-t border-white/10 pt-5">
        {MEMBERSHIP_HIGHLIGHTS.map((feature) => (
          <li className="flex items-start gap-2 text-sm text-white/85" key={feature}>
            <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#FD954F]" />
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      <button
        className="mt-auto min-h-12 w-full rounded-xl bg-[#FD954F] px-4 py-3 text-sm font-bold text-[#1C1C1E] transition-colors hover:bg-[#F28747] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FD954F] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0F0F14] disabled:cursor-not-allowed disabled:opacity-60"
        disabled={disabled || isLoading}
        onClick={() => onPurchase(packageType)}
        type="button"
      >
        {isLoading
          ? "Preparing checkout…"
          : showFreeTrial
            ? "Start 3-day trial"
            : `Start ${pkg.displayName.toLowerCase()} membership`}
      </button>
    </article>
  );
}
